import React, { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import LoadingDots from 'components/Common/Loaders/LoadingDots';
import { BodyType } from '../../types';
import { parseDefaultValue, parseStreamingValue } from 'helpers/htmlParser';
import { getMarkedHtmlFromSelection } from 'helpers/getMarkedHtmlFromSelection';

const Body: FC<BodyType> = ({
    defaultValue,
    streamingValue,
    isGeneratingForThis,
    setCopyMenu,
    setContextMenu,
    contextMenuShow,
    copyMenu,
    isFullScreenMode,
    isROne,
    ROneOutput,
    disableContextMenu = false,
}) => {
    const [isScrolled, setIsScrolled] = useState<boolean>(false);
    const [streamingStatus, setStreamingStatus] = useState<string>('');
    const answerContainer = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (streamingValue) {
            setStreamingStatus('start');
        }
        if (streamingStatus === 'start' && !streamingValue) {
            setStreamingStatus('finish');
        }
    }, [streamingValue, streamingStatus]);

    useEffect(() => {
        if (isFullScreenMode && answerContainer.current) {
            answerContainer.current.scrollTop = 0;
        }
    }, [answerContainer, isFullScreenMode]);

    useEffect(() => {
        if (!streamingValue && !defaultValue) {
            return;
        }

        if (answerContainer.current) {
            if (streamingStatus === 'finish' && !isScrolled) {
                answerContainer.current.scrollTop = answerContainer.current.scrollHeight;
                return;
            }

            if (
                answerContainer.current.scrollHeight > answerContainer.current.clientHeight &&
                answerContainer.current.scrollHeight - answerContainer.current.scrollTop >
                    answerContainer.current.clientHeight + 150
            ) {
                setIsScrolled(true);
                return;
            }
            setIsScrolled(false);
            answerContainer.current.scrollTop = answerContainer.current.scrollHeight;
        }
    }, [streamingValue, defaultValue, isScrolled, streamingStatus, answerContainer]);

    const showResponse = (): React.ReactNode => {
        if (isGeneratingForThis) {
            return <LoadingDots />;
        }

        if (defaultValue) {
            return parseDefaultValue(defaultValue);
        }

        if (streamingValue) {
            return parseStreamingValue(streamingValue);
        }

        return null;
    };

    const handleContextMenu = async (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
        if (disableContextMenu) {
            return;
        }

        const selection = window.getSelection();
        const selectionText = selection?.toString();
        if (!selectionText || !selection || selection.rangeCount === 0) {
            return;
        }

        e.preventDefault();
        let { pageX, pageY } = e;
        if (pageX + 350 > window.innerWidth) {
            pageX = window.innerWidth - 350;
        }

        if (pageY + 115 > window.innerHeight) {
            pageY = window.innerHeight - 115;
        }

        // Get marked HTML directly from selection
        let markedHtml = selectionText;
        if (answerContainer.current) {
            markedHtml = getMarkedHtmlFromSelection(
                answerContainer.current,
                selection.getRangeAt(0),
            );
        }

        setContextMenu({
            x: pageX,
            y: pageY,
            value: selectionText,
            markedHtml,
            show: true,
        });
    };

    const onMouseDown = (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
        const { pageX, pageY } = e;
        setCopyMenu((prev) => ({ ...prev, x: pageX, y: pageY }));
    };

    const onSelectText = (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
        if (contextMenuShow) {
            return;
        }
        const selection = window.getSelection()?.toString();

        if (!selection) {
            return;
        }
        e.preventDefault();
        let { x, y } = copyMenu;
        const { pageX, pageY } = e;
        if (pageY < y) {
            y = pageY;
            x = pageX;
        }
        y -= 60;

        if (x + 200 > window.innerWidth) {
            x = window.innerWidth - 200;
        }
        if (y + 45 > window.innerHeight) {
            y = window.innerHeight - 45;
        }

        setCopyMenu(() => ({
            x,
            y,
            value: selection,
            show: true,
        }));
    };
    return (
        <Box
            className="outputed-text-value scrollbar-on-hover"
            onContextMenu={handleContextMenu}
            ref={answerContainer}
            onMouseUp={onSelectText}
            onMouseDown={onMouseDown}
        >
            {isROne ? ROneOutput : showResponse()}
        </Box>
    );
};

export default Body;
